import React from "react";
import LegalLayout from "../../components/LegalLayout";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { Lock, Database, Globe, ShieldCheck } from "lucide-react";

const SECTIONS = [
  { id: "info-collect",  label: "Information collected" },
  { id: "how-used",      label: "How we use it" },
  { id: "legal-bases",   label: "Legal bases (GDPR)" },
  { id: "sharing",       label: "How we share" },
  { id: "international", label: "International transfers" },
  { id: "retention",     label: "Retention" },
  { id: "rights",        label: "Your rights" },
  { id: "children",      label: "Children" },
  { id: "security",      label: "Security" },
  { id: "changes",       label: "Changes" },
  { id: "contact",       label: "Contact" },
];

const PILLARS = [
  { icon: Lock,        title: "GDPR + UK GDPR + CCPA", desc: "We treat every region's privacy law as a floor, not a ceiling. Standard Contractual Clauses for cross-border transfers.", accent: "var(--qt-cyan)" },
  { icon: Database,    title: "Data minimisation",     desc: "We collect only what we need to scan, audit, bill, and secure. IPs are hashed and truncated for rate-limiting and abuse prevention.", accent: "#1FDD83" },
  { icon: Globe,       title: "Sub-processor list",    desc: "Stripe, Resend, Cloudflare, PostHog (EU host), MongoDB Atlas — published below with regions and purposes.", accent: "var(--qt-cyan)" },
  { icon: ShieldCheck, title: "Your rights, on demand", desc: "Access, correct, delete, port, restrict, object — email privacy@qortrace.com and we respond within 30 days.", accent: "var(--qt-amber)" },
];

export default function Privacy() {
  useDocumentTitle("Privacy Policy — QorTrace");
  return (
    <LegalLayout
      eyebrow="LEGAL · PRIVACY"
      title="Privacy Policy"
      lastUpdated="February 2026"
      version="qortrace-privacy-v1.0"
      status="ACTIVE"
      intro="This Privacy Policy explains how QorTrace collects, uses, shares, and protects personal information when you use our web app, APIs, certificates, and related services. We've written it in plain English where possible — and a structured table of contents on the right lets you jump to the section that matters."
      stats={[
        { value: "11", label: "SECTIONS" },
        { value: "GDPR", label: "+ UK GDPR + CCPA" },
        { value: "30d", label: "RIGHT-REQUEST SLA" },
        { value: "0",   label: "DATA SOLD · EVER" },
      ]}
      pillars={PILLARS}
      sections={SECTIONS}
    >
      <div className="qt-legal-callout">
        <strong>Placeholders.</strong> Replace <code>{"{COMPANY_ENTITY}"}</code>,{" "}
        <code>{"{COMPANY_ADDRESS}"}</code>, and{" "}
        <code>{"{EU_REPRESENTATIVE}"}</code> with your registered legal entity,
        principal place of business, and (if you offer the Service to EU/UK
        residents) the name and address of your appointed Article 27 / UK GDPR
        representative.
      </div>

      <p>
        This Privacy Policy explains how <strong>{"{COMPANY_ENTITY}"}</strong>{" "}
        (&ldquo;<strong>QorTrace</strong>,&rdquo; &ldquo;<strong>we</strong>,&rdquo;
        &ldquo;<strong>us</strong>&rdquo;) collects, uses, shares, and protects
        personal information when you use the QorTrace web app, APIs,
        certificates, and related services (the &ldquo;
        <strong>Service</strong>&rdquo;).
      </p>
      <p>
        For purposes of the EU/UK General Data Protection Regulation (GDPR /
        UK&nbsp;GDPR), QorTrace is the <strong>controller</strong> of personal
        data we collect about you in connection with the Service. For purposes
        of the California Consumer Privacy Act / California Privacy Rights Act
        (CCPA/CPRA), QorTrace is the <strong>business</strong>.
      </p>

      <h2 id="info-collect">1. Information We Collect</h2>

      <h3>(a) Account &amp; identity data</h3>
      <ul>
        <li>Name, email address, password hash, organization, role.</li>
        <li>Two-factor authentication (TOTP) secret, recovery codes.</li>
        <li>Marketing-cadence preferences and unsubscribe state.</li>
      </ul>

      <h3>(b) Customer Content you submit</h3>
      <ul>
        <li>Wallet addresses you scan and the chains they belong to.</li>
        <li>
          Smart-contract source code, GitHub repository URLs, and uploaded zip
          archives submitted for audit.
        </li>
        <li>
          Project names, contract addresses, contact details, and logos for
          co-branded certificates.
        </li>
      </ul>

      <h3>(c) Billing data</h3>
      <ul>
        <li>
          Stripe customer ID, plan, seat count, transaction history. We do not
          store full card numbers; payments are tokenized and processed
          directly by Stripe.
        </li>
      </ul>

      <h3>(d) Service usage &amp; technical data</h3>
      <ul>
        <li>
          IP address (hashed and truncated for rate limiting and abuse
          prevention), user-agent, language, referrer.
        </li>
        <li>
          Pages viewed, features used, audit and certificate events, signed-in
          status.
        </li>
        <li>Server logs, error reports, and request IDs for diagnostics.</li>
      </ul>

      <h3>(e) Cookies &amp; similar technologies</h3>
      <p>
        See our <a href="/legal/cookies">Cookie Policy</a> for the complete
        list and your control options. By default we set only strictly
        necessary cookies; analytics cookies are used only after you grant
        consent via our cookie banner.
      </p>

      <h2 id="how-used">2. How We Use Information</h2>
      <ul>
        <li>
          <strong>Provide the Service</strong> &mdash; run scans and audits,
          generate certificates and reports, deliver continuous monitoring
          alerts, and operate the customer dashboard.
        </li>
        <li>
          <strong>Account &amp; security</strong> &mdash; authenticate you,
          protect against brute-force attacks, prevent fraud and abuse,
          maintain audit trails.
        </li>
        <li>
          <strong>Billing</strong> &mdash; process payments, calculate metered
          usage, send invoices and receipts.
        </li>
        <li>
          <strong>Service communications</strong> &mdash; send transactional
          emails (audit delivery, certificate delivery, password reset,
          billing receipts).
        </li>
        <li>
          <strong>Product improvement</strong> &mdash; aggregated, de-identified
          analytics to understand which features are valuable and where users
          encounter friction (only with your consent).
        </li>
        <li>
          <strong>Marketing</strong> &mdash; only when you opt in; you can
          un-subscribe per-cadence or globally from{" "}
          <a href="/account/settings">/account/settings</a>.
        </li>
        <li>
          <strong>Legal &amp; compliance</strong> &mdash; comply with applicable
          law, respond to lawful requests, defend our rights.
        </li>
      </ul>

      <h2 id="legal-bases">3. Legal Bases (GDPR / UK&nbsp;GDPR)</h2>
      <ul>
        <li>
          <strong>Contract</strong> &mdash; to provide the Service you have
          requested (running audits, delivering reports, processing payments).
        </li>
        <li>
          <strong>Legitimate interest</strong> &mdash; security monitoring,
          fraud prevention, basic service-improvement analytics where balanced
          against your rights.
        </li>
        <li>
          <strong>Consent</strong> &mdash; analytics and marketing cookies,
          marketing emails. You may withdraw consent at any time; this does
          not affect the lawfulness of prior processing.
        </li>
        <li>
          <strong>Legal obligation</strong> &mdash; tax records, accounting,
          responding to lawful regulatory requests.
        </li>
      </ul>

      <h2 id="sharing">4. How We Share Information</h2>
      <p>
        We share personal information only with the categories of recipients
        below, and only as necessary for the stated purpose:
      </p>
      <table className="qt-legal-table">
        <thead>
          <tr>
            <th>Recipient</th>
            <th>Purpose</th>
            <th>Region</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Stripe, Inc.</td>
            <td>Payment processing and tax calculation</td>
            <td>USA</td>
          </tr>
          <tr>
            <td>Resend</td>
            <td>Transactional and opt-in marketing email delivery</td>
            <td>USA</td>
          </tr>
          <tr>
            <td>Cloudflare, Inc.</td>
            <td>SSL termination, edge caching, DDoS mitigation</td>
            <td>Global</td>
          </tr>
          <tr>
            <td>PostHog</td>
            <td>
              Product analytics (only with your consent; EU host available)
            </td>
            <td>USA or EU</td>
          </tr>
          <tr>
            <td>MongoDB Atlas</td>
            <td>Primary database hosting</td>
            <td>Region of your choice</td>
          </tr>
          <tr>
            <td>Hosting provider</td>
            <td>Application compute and storage</td>
            <td>USA</td>
          </tr>
        </tbody>
      </table>
      <p>
        We never sell your personal information. We will share information
        with law enforcement only where compelled by valid legal process.
      </p>

      <h2 id="international">5. International Transfers</h2>
      <p>
        QorTrace is operated from the United States. If you access the Service
        from outside the U.S., your information will be transferred to and
        processed in the U.S. and other jurisdictions where our service
        providers operate. Where transfers are subject to GDPR / UK&nbsp;GDPR,
        we rely on Standard Contractual Clauses or other lawful transfer
        mechanisms with each sub-processor.
      </p>

      <h2 id="retention">6. Retention</h2>
      <ul>
        <li>
          <strong>Account data</strong> &mdash; for the lifetime of your
          account plus up to 12 months after deletion for billing and audit-log
          continuity.
        </li>
        <li>
          <strong>Audit submissions and reports</strong> &mdash; retained for
          the lifetime of your account so you can re-download. You may delete
          individual audits from your dashboard.
        </li>
        <li>
          <strong>Server logs</strong> &mdash; up to 30 days for diagnostics
          and security.
        </li>
        <li>
          <strong>Marketing preferences</strong> &mdash; retained on a
          permanent suppression list so we honor unsubscribes even after
          account deletion.
        </li>
      </ul>

      <h2 id="rights">7. Your Rights</h2>
      <p>
        Depending on your jurisdiction, you may have rights to access, correct,
        delete, port, or restrict the processing of your personal information,
        and to object to processing or withdraw consent. EU/UK residents have
        the right to lodge a complaint with their supervisory authority.
        California residents have rights under CCPA/CPRA, including the right
        to opt out of &ldquo;sharing&rdquo; for cross-context behavioral
        advertising (we do not engage in such sharing today).
      </p>
      <p>
        To exercise any right, email{" "}
        <a href="mailto:privacy@qortrace.com">privacy@qortrace.com</a>. We
        respond within 30 days; we may need to verify your identity before
        acting on a request.
      </p>

      <h2 id="children">8. Children</h2>
      <p>
        The Service is not directed to children under 16, and we do not
        knowingly collect personal information from them. If you believe a
        child has provided us information, please contact{" "}
        <a href="mailto:privacy@qortrace.com">privacy@qortrace.com</a> and we
        will delete it.
      </p>

      <h2 id="security">9. Security</h2>
      <p>
        We employ industry-standard administrative, technical, and physical
        safeguards: TLS 1.2+ in transit, encryption at rest at the database
        layer, role-based access control, mandatory two-factor authentication
        on admin accounts, and rate-limited APIs. No system is perfectly
        secure; we maintain an active{" "}
        <a href="/legal/security">responsible-disclosure program</a> and
        encourage security researchers to participate.
      </p>

      <h2 id="changes">10. Changes</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes
        will be communicated by email or in-app banner at least seven (7) days
        before they take effect. The &ldquo;Last updated&rdquo; date at the
        top of this page reflects the most recent revision.
      </p>

      <h2 id="contact">11. Contact &amp; EU Representative</h2>
      <p>
        Email <a href="mailto:privacy@qortrace.com">privacy@qortrace.com</a>{" "}
        or write to <strong>{"{COMPANY_ENTITY}, {COMPANY_ADDRESS}"}</strong>.
        Our EU/UK Article&nbsp;27 representative is{" "}
        <strong>{"{EU_REPRESENTATIVE}"}</strong>.
      </p>
    </LegalLayout>
  );
}
